/*
  maquette.js: pop-stamp.fr x addaxa-mockup, free mockup request (ported from plaque-deces).
  Vanilla ES5, no dependency, loaded with `defer` on product and collection pages (layout/theme.liquid).
  Config in window.popMaquette: `enabled` (snippets/maquette-gate.liquid), then
  { preview, whatsapp, locale, phone, t } (snippets/maquette-request.liquid, popup part).

  Flow: [data-maquette-open] opens the popup → text and/or files → capture of the product form at
  send time (variant, quantity, Easify properties linked with form="") → POST /apps/maquette/requests
  (JSON, or multipart `payload` + `files` ≤ 5 × 10 MB) → reference.
  « En repartant de zéro »: the popup's own options (snippets/maquette-scratch.liquid) instead of the product form,
  with the model picked in the gallery as the product, else « Importer mon visuel ».
  WhatsApp on a touch device: tab pre-opened within the click, then navigated to whatsappUrl.
  WhatsApp on a computer: QR code, GET /apps/maquette/requests/:reference polled until the message arrives.
  Anti-duplicate: same text + variant + channel + email + files + properties within 10 min reuses the response.
  Preview mode (backend not connected): nothing is sent, a fake response is returned.
*/
(function (root) {
  'use strict';

  var pop = root.popMaquette = root.popMaquette || {};
  if (pop.__bound) return;
  pop.__bound = true;

  var doc = root.document;
  var ENDPOINT = '/apps/maquette/requests';
  var MAX_FILES = 5;
  var MAX_FILE_SIZE = 10 * 1024 * 1024;
  var MAX_TEXT = 5000;
  var COUNTER_FROM = 4500;
  var DUPLICATE_MS = 10 * 60 * 1000;
  var FILE_EXTENSIONS = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|pdf)$/i;
  var PROPERTY_PATTERN = /^properties\[(.+)\]$/;
  // Easify option values end with their price, e.g. "Rond [+2,00 €]": removed for display only
  // Every Easify price « [ +6,90€ ] » of a value (an ink list holds one per colour)
  var PRICE_SUFFIX = /\s*\[[^\]]*\]/g;
  // The Easify upload property holds the URL of the file Easify already uploaded
  var FILE_URL = /^https?:\/\//i;
  // Easify toggle that only reveals the ink list (« Voir 33 coloris disponibles »): the inks themselves are shown.
  // « Type » (Presse / Tampon): the device line already says it. Easify names them in the market's language.
  var SUMMARY_HIDDEN = ['Ajouter un encreur', 'Add an ink pad', 'Stempelkissen hinzufügen', 'Aggiungi un tampone d\'inchiostro', 'Añadir un tampón de tinta', 'Type', 'Typ', 'Tipo'];
  var STORAGE_KEY = 'popMaquetteRequest';
  var QR_POLL_MS = 4000;
  var QR_HINT_MS = 90000;
  var QR_POLL_MAX_MS = 15 * 60000;
  var PREVIEW_DELAY_MS = 1200;
  var PREVIEW_RECEIVED_POLL = 3;
  // Model gallery: tiles added per « Afficher plus », products per storefront JSON page
  var MODELS_STEP = 8;
  var MODELS_PAGE = 48;
  var FOCUSABLE ='a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // mode: 'product' ("Depuis ce produit", the customization goes along) | 'scratch' (the « Importer mon visuel » options)
  // model: the scratch tab's model choice and gallery
  var request = { files: [], previews: [], trigger: null, mode: 'product', model: { picked: null } };
  var qrPoll = null;
  var qrLibrary = null;
  var previewPolls = 0;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function t(key) {
    return (pop.t && pop.t[key]) || '';
  }

  // %{key} placeholder; split/join so a `$` in a file name or email is kept as is
  function fill(text, key, value) {
    return text.split('%{' + key + '}').join(value);
  }

  function truncate(value, max) {
    var text = value === null || value === undefined ? '' : String(value);
    return text.length > max ? text.slice(0, max) : text;
  }

  function hashString(input) {
    var hash = 5381;
    for (var i = 0; i < input.length; i += 1) hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
    return (hash >>> 0).toString(36);
  }

  function qs(selector, scope) { return (scope || doc).querySelector(selector); }
  function qsa(selector, scope) { return Array.prototype.slice.call((scope || doc).querySelectorAll(selector)); }

  function el(tag, attrs, text) {
    var node = doc.createElement(tag);
    Object.keys(attrs).forEach(function (key) {
      if (key === 'className') node.className = attrs[key];
      else node.setAttribute(key, attrs[key]);
    });
    if (text) node.textContent = text;
    return node;
  }

  function show(node, visible) {
    if (!node) return;
    if (visible) node.removeAttribute('hidden');
    else node.setAttribute('hidden', '');
  }

  function focus(node, options) {
    if (!node) return;
    try { node.focus(options); } catch (e) { /* focus refused */ }
  }

  // Laid out: neither display:none nor inside a hidden parent
  function isShown(node) { return node.getClientRects().length > 0; }

  // A <script type="application/json"> of the page, null when absent or broken
  function readJson(selector) {
    var script = qs(selector);
    try { return script ? JSON.parse(script.textContent) : null; } catch (e) { return null; }
  }

  // « Texte personnalisé 1 » of name="properties[Texte personnalisé 1]", '' for any other field
  function propertyName(field) {
    var match = PROPERTY_PATTERN.exec(field.getAttribute('name') || '');
    return match ? match[1] : '';
  }

  // A Shopify CDN image at that width
  function withWidth(url, width) { return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width; }

  function escapeHtml(value) {
    var node = doc.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }

  // ---------------------------------------------------------------------------
  // Product context
  // ---------------------------------------------------------------------------

  // Printed by the entry snippet (the theme does not print the product as JSON)
  function productData() {
    var product = readJson('[data-maquette-product]');
    return product && typeof product === 'object' ? product : null;
  }

  function findVariant(product, variantId) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    for (var i = 0; i < variants.length; i += 1) {
      if (variants[i] && Number(variants[i].id) === variantId) return variants[i];
    }
    return null;
  }

  // Readable options "Taille : 4 cm" from product.options + variant.option1..3
  function variantOptions(product, variant) {
    var out = {};
    if (!variant) return out;
    (Array.isArray(product.options) ? product.options : []).forEach(function (name, index) {
      var label = typeof name === 'string' ? name : (name && name.name) || ('Option ' + (index + 1));
      var value = variant['option' + (index + 1)];
      if (value === undefined || value === null || value === '') return;
      if (label === 'Title' && value === 'Default Title') return;
      out[truncate(label, 255)] = truncate(value, 255);
    });
    return out;
  }

  function variantImage(product, variant) {
    var image = variant && variant.featured_image && (variant.featured_image.src || variant.featured_image.url);
    if (!image) image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);
    if (!image) return null;
    var url = typeof image === 'string' ? image : (image.src || image.url || '');
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url ? truncate(url, 2000) : null;
  }

  // Page sent to the backend ('product' | 'home' | 'cart'): the trigger's data-maquette-page, else 'product'
  function requestPage() {
    var page = request.trigger && request.trigger.getAttribute('data-maquette-page');
    return page === 'home' || page === 'cart' ? page : 'product';
  }

  // Snapshot of the product form #product-form-{id} (`form.id` would return the <input name="id">).
  // Easify inputs sit OUTSIDE the form and are linked to it with form="…": the native `field.form`
  // follows that attribute, so filtering on it catches them and the inputs inside the form alike.
  // Disabled inputs are hidden conditional options. `visual`: an Easify upload is filled.
  // "En repartant de zéro": the popup's own options instead (scratchSnapshot).
  function captureSnapshot() {
    var out = { product: null, properties: {}, visual: false };
    // Listing card (data-maquette-page="home"): no product, even if the page holds a product form
    if (requestPage() !== 'product') return out;
    if (request.mode === 'scratch') return scratchSnapshot(out);
    var product = productData();
    if (!product || !product.handle) return out;
    var formId = 'product-form-' + product.id;
    var form = doc.getElementById(formId);
    var idField = form && form.querySelector('[name="id"]');
    var variantId = idField && idField.value ? Number(idField.value) : NaN;
    var variant = findVariant(product, variantId) || (!isFinite(variantId) && Array.isArray(product.variants) ? product.variants[0] : null);
    var quantityField = form && (form.querySelector('[name="quantity"]') || qs('[name="quantity"][form="' + formId + '"]'));
    var quantity = quantityField ? parseInt(quantityField.value, 10) : 1;
    if (variant || isFinite(variantId)) {
      out.product = {
        handle: truncate(product.handle, 255),
        title: truncate(product.title, 255),
        variantId: variant ? Number(variant.id) : variantId,
        variantTitle: variant && variant.title && variant.title !== 'Default Title' ? truncate(variant.title, 255) : null,
        options: variantOptions(product, variant),
        price: variant && typeof variant.price === 'number' ? variant.price : null,
        image: variantImage(product, variant),
        quantity: quantity > 0 ? quantity : 1
      };
      if (typeof product.id === 'number') out.product.productId = product.id;
    }
    if (!form) return out;

    qsa('[name^="properties"]').forEach(function (field) {
      if (field.form !== form || field.disabled) return;
      var name = propertyName(field);
      // `_` names are app internals (_tpo_add_by, _Visuel personnalisé_original-1)
      if (!name || name.charAt(0) === '_') return;
      var type = (field.getAttribute('type') || '').toLowerCase();
      // Easify uploads the file itself and stores its URL in a text property
      if (type === 'file') return;
      if ((type === 'radio' || type === 'checkbox') && !field.checked) return;
      var value = String(field.value || '').trim();
      if (!value) return;
      if (FILE_URL.test(value)) out.visual = true;
      out.properties[truncate(name, 255)] = truncate(value, 5000);
    });
    return out;
  }

  // Chosen option values without their price (not the files nor the « Texte personnalisé » boxes), else the variant options
  function optionValues(snapshot) {
    var values = [];
    var texts = customTextFields().map(propertyName);
    Object.keys(snapshot.properties).forEach(function (key) {
      var value = snapshot.properties[key];
      if (!FILE_URL.test(value) && texts.indexOf(key) === -1 && SUMMARY_HIDDEN.indexOf(key) === -1) values.push(value.replace(PRICE_SUFFIX, '').trim());
    });
    if (!values.length && snapshot.product) {
      var options = snapshot.product.options;
      Object.keys(options).forEach(function (key) { values.push(options[key]); });
    }
    return values.filter(Boolean);
  }

  // Visible Easify text boxes of the product form (« Texte personnalisé 1…4 »: the hidden ones are
  // lines the model does not use), mirrored in the popup above « Votre demande »
  function customTextFields() {
    var product = requestPage() === 'product' ? productData() : null;
    var form = product && doc.getElementById('product-form-' + product.id);
    if (!form || request.mode === 'scratch') return [];
    return qsa('input.tpo_text-box[name^="properties"]').filter(function (field) {
      return field.form === form && !field.disabled && isShown(field);
    });
  }

  function hasCustomText() {
    return customTextFields().some(function (field) { return !!field.value.trim(); });
  }

  // Popup → page: Easify listens to these events (its preview, the cart property)
  function writeField(field, value) {
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // One input per box, with the page's label (once for consecutive lines), holding what the box holds
  // (the model example included: kept unless the customer changes it)
  function renderTexts(modal) {
    var box = qs('[data-maquette-request-texts]', modal);
    if (!box) return;
    box.innerHTML = '';
    var fields = customTextFields();
    var lastLabel = null;
    fields.forEach(function (field, index) {
      var labelNode = field.closest('.tpo_option-label');
      var label = (labelNode && labelNode.textContent.trim()) || propertyName(field).replace(/\s+\d+$/, '');
      var id = 'maquette-custom-text-' + index;
      if (label !== lastLabel) box.appendChild(el('label', { className: 'maquette__label', for: id }, label));
      lastLabel = label;
      var input = el('input', { type: 'text', id: id, className: 'maquette__input', 'aria-label': label, placeholder: field.placeholder || '' });
      if (field.maxLength > 0) input.maxLength = field.maxLength;
      input.value = field.value;
      input.addEventListener('input', function () { writeField(field, input.value); });
      box.appendChild(input);
    });
    show(box, fields.length > 0);
    // Product with text boxes: the texts are the request, no file to add (the scratch tab keeps it)
    show(qs('.maquette__files', modal), fields.length === 0);
    if (fields.length && request.files.length) clearFiles(modal);
  }

  // ---------------------------------------------------------------------------
  // Files
  // ---------------------------------------------------------------------------

  function isAllowedFile(file) {
    var type = String(file.type || '');
    if (/^image\//.test(type) || type === 'application/pdf') return true;
    return !type && FILE_EXTENSIONS.test(String(file.name || ''));
  }

  // ≤ 5 files, ≤ 10 MB each, images or PDF: same limits as the proxy
  function validateFiles(files) {
    var accepted = [];
    var rejected = [];
    files.forEach(function (file) {
      var name = file.name || 'file';
      if (accepted.length >= MAX_FILES) rejected.push(fill(t('file_count'), 'name', name));
      else if (file.size > MAX_FILE_SIZE) rejected.push(fill(t('file_size'), 'name', name));
      else if (!isAllowedFile(file)) rejected.push(fill(t('file_type'), 'name', name));
      else accepted.push(file);
    });
    return { accepted: accepted, message: rejected.join(' ') };
  }

  function formatBytes(bytes) {
    if (bytes >= 1024 * 1024) {
      var mb = (bytes / 1024 / 1024).toFixed(1);
      return (pop.locale === 'en' ? mb : mb.replace('.', ',')) + ' ' + t('unit_mb');
    }
    return Math.max(1, Math.round(bytes / 1024)) + ' ' + t('unit_kb');
  }

  function revokePreviews() {
    request.previews.forEach(function (url) {
      try { root.URL.revokeObjectURL(url); } catch (e) { /* already revoked */ }
    });
    request.previews = [];
  }

  function renderPreviews(modal) {
    var list = qs('[data-maquette-request-previews]', modal);
    list.innerHTML = '';
    revokePreviews();
    request.files.forEach(function (file, index) {
      var visual;
      if (/^image\//.test(file.type || '') && root.URL && typeof root.URL.createObjectURL === 'function') {
        var url = root.URL.createObjectURL(file);
        request.previews.push(url);
        visual = el('img', { className: 'maquette__preview-image', src: url, alt: '' });
      } else {
        visual = el('span', { className: 'maquette__preview-image maquette__preview-image--file' }, 'PDF');
      }
      var item = el('li', { className: 'maquette__preview' });
      item.appendChild(visual);
      item.appendChild(el('span', { className: 'maquette__preview-name' }, file.name + '  ' + formatBytes(file.size)));
      item.appendChild(el('button', { type: 'button', className: 'maquette__preview-remove', 'data-maquette-request-remove': String(index), 'aria-label': t('remove') + ' ' + file.name }, t('remove')));
      list.appendChild(item);
    });
    show(list, request.files.length > 0);
  }

  function setMessage(node, message) {
    node.textContent = message || '';
    show(node, !!message);
  }

  function clearFiles(modal) {
    request.files = [];
    renderPreviews(modal);
  }

  function addFiles(modal, files) {
    var validated = validateFiles(request.files.concat(Array.prototype.slice.call(files)));
    request.files = validated.accepted;
    setMessage(qs('[data-maquette-request-files-error]', modal), validated.message);
    renderPreviews(modal);
  }

  // ---------------------------------------------------------------------------
  // Network
  // ---------------------------------------------------------------------------

  // Never rejected: { ok, status, data } (status 0 = network error)
  function fetchJson(url, opts) {
    if (pop.preview) return previewFetch(url, opts);
    opts.credentials = 'same-origin';
    opts.headers = opts.headers || {};
    opts.headers.Accept = 'application/json';
    return root.fetch(url, opts).then(function (response) {
      return response.json().then(null, function () { return null; }).then(function (data) {
        return { ok: response.ok, status: response.status, data: data };
      });
    }, function () {
      return { ok: false, status: 0, data: null };
    });
  }

  // Preview mode (backend not connected): no network at all. The POST waits, logs what would be
  // sent and returns a fake reference; the QR poll answers « received » on the 3rd call.
  function previewFetch(url, opts) {
    if (opts.method === 'GET') {
      previewPolls += 1;
      return Promise.resolve({ ok: true, status: 200, data: { received: previewPolls >= PREVIEW_RECEIVED_POLL } });
    }
    var body = opts.body;
    var files = [];
    if (typeof body === 'string') body = JSON.parse(body);
    else {
      files = body.getAll('files');
      body = JSON.parse(body.get('payload'));
    }
    root.console.info('[maquette preview]', body, files);
    var data = { id: 'preview', reference: 'APERCU-' + Math.random().toString(36).slice(2, 6).toUpperCase(), promise: '24h' };
    if (body.channel === 'whatsapp') {
      var number = String(pop.whatsapp || '').replace(/\D/g, '') || '33000000000';
      data.whatsappUrl = 'https://wa.me/' + number + '?text=' + encodeURIComponent(t('preview_wa_text'));
    }
    return new Promise(function (resolve) {
      setTimeout(function () { resolve({ ok: true, status: 201, data: data }); }, PREVIEW_DELAY_MS);
    });
  }

  // The server detail (zod, English) is never shown to the customer
  function requestErrorMessage(status) {
    var key = 'err_default';
    if (status === 413) key = 'err_413';
    else if (status === 400 || status === 422) key = 'err_400';
    else if (status === 429) key = 'err_429';
    else if (status === 401 || status === 403) key = 'err_403';
    else if (status === 0) key = 'err_network';
    var message = t(key);
    if (pop.phone && (key === 'err_429' || key === 'err_403' || key === 'err_default')) {
      message += ' ' + fill(t('call_us'), 'phone', pop.phone);
    }
    return message;
  }

  function readMemo() {
    try { return JSON.parse(root.sessionStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }

  function writeMemo(memo) {
    try { root.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memo)); } catch (e) { /* private browsing: no memory */ }
  }

  function closeWindow(win) {
    try { if (win && !win.closed) win.close(); } catch (e) { /* window already closed */ }
  }

  // WhatsApp window: pre-opened in the click handler (popup blockers), navigated after the response
  function navigateWhatsapp(win, url) {
    if (!url) {
      closeWindow(win);
      return 'none';
    }
    if (win && !win.closed) {
      try {
        win.location.href = url;
        return 'preopened';
      } catch (e) { /* window unreachable: link shown */ }
    }
    closeWindow(win);
    return 'blocked';
  }

  // QR code: greeting + reference only (the request text is already stored), so the code stays easy to scan
  function shortWhatsappUrl(url) {
    var match = /^([^?]+)\?text=(.*)$/.exec(String(url || ''));
    if (!match) return url;
    try {
      return match[1] + '?text=' + encodeURIComponent(decodeURIComponent(match[2]).split('\n')[0]);
    } catch (e) {
      return url;
    }
  }

  // Resolves { response, duplicate, whatsapp: 'preopened'|'blocked'|'none'|null } or { error }.
  // `popup`: WhatsApp tab already opened by the caller, synchronously within the click.
  function sendRequest(channel, email, text, snapshot, files, popup) {
    var now = Date.now();
    var variantId = snapshot.product ? snapshot.product.variantId : '';
    // Easify texts and the files are part of what makes two requests the same
    var attached = files.map(function (file) { return file.name + ':' + file.size; }).join(',') + '|' + hashString(JSON.stringify(snapshot.properties));
    var key = [text, variantId, channel, email, attached].join('|');

    // Local anti-duplicate (10 min): the response is reused without network. Not in preview mode.
    var memo = pop.preview ? null : readMemo();
    if (memo && memo.key === key && memo.response && now - memo.at < DUPLICATE_MS) {
      return Promise.resolve({ response: memo.response, duplicate: true, whatsapp: channel === 'whatsapp' ? navigateWhatsapp(popup, memo.response.whatsappUrl) : null });
    }

    // Server anti-duplicate: fingerprint of text + variant + minute (stable within the same minute), ≤ 128 characters
    var minute = Math.floor(now / 60000);
    var base = text + '|' + variantId + '|' + minute + '|' + attached;
    var context = { page: requestPage(), pageUrl: truncate(root.location.href, 2000) };
    var locale = (root.popstampStore && root.popstampStore.lang) || pop.locale || doc.documentElement.lang;
    if (locale) context.locale = truncate(String(locale).trim(), 8);
    if (snapshot.product) context.product = snapshot.product;
    if (Object.keys(snapshot.properties).length) context.properties = snapshot.properties;
    // No model picked: the editor opens on its model picker. A picked model is the request's product, as on its page.
    if (request.mode === 'scratch' && !request.model.picked) context.designSource = 'scratch';
    var body = {
      channel: channel,
      text: truncate(text, MAX_TEXT),
      context: context,
      signature: 'pop-' + hashString(base) + '-' + hashString(base.split('').reverse().join('')) + '-' + minute.toString(36)
    };
    if (email) body.email = truncate(email, 255);

    // JSON without files; otherwise multipart `payload` (JSON) + `files` (n × File)
    var opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    if (files.length) {
      opts.headers = {};
      opts.body = new root.FormData();
      opts.body.append('payload', JSON.stringify(body));
      files.forEach(function (file) { opts.body.append('files', file, file.name || 'file'); });
    }

    return fetchJson(ENDPOINT, opts).then(function (result) {
      if (!result.ok || !result.data || !result.data.reference) {
        closeWindow(popup);
        return { error: requestErrorMessage(result.status) };
      }
      if (!pop.preview) writeMemo({ key: key, response: result.data, at: now });
      return { response: result.data, duplicate: false, whatsapp: channel === 'whatsapp' ? navigateWhatsapp(popup, result.data.whatsappUrl) : null };
    }).then(null, function () {
      closeWindow(popup);
      return { error: requestErrorMessage(0) };
    });
  }

  // ---------------------------------------------------------------------------
  // Modal: focus trap, Esc, overlay, return focus, locked while sending
  // ---------------------------------------------------------------------------

  function requestModal() { return qs('[data-maquette-modal]'); }

  function isOpen(modal) { return !!modal && !modal.hasAttribute('hidden'); }

  function isSending(modal) {
    return !qs('[data-maquette-request-state="sending"]', modal).hasAttribute('hidden');
  }

  // The page is locked behind the popup (body.maquette-open, assets/maquette.css): its scrollbar goes and the body
  // takes the width back in padding (--maquette-scrollbar), so nothing shifts and the overlay and the popup's frame
  // reach the screen's edge. Measured before locking: 0 with overlay scrollbars and on phones; a reopening during
  // the slide out keeps the width measured first.
  function lockPage(locked) {
    var html = doc.documentElement;
    if (locked && !doc.body.classList.contains('maquette-open')) {
      html.style.setProperty('--maquette-scrollbar', Math.max(0, root.innerWidth - html.clientWidth) + 'px');
    }
    doc.body.classList.toggle('maquette-open', locked);
  }

  // Extruding popup (assets/component-popup.css): --opened shows the wrapper, data-popup-opened slides the panel
  // in and the theme's overlay fades in (--visible); the panel slides out (300ms, assets/maquette.css) before the
  // wrapper is hidden again. The page is given back only then: its scrollbar coming back during the slide would
  // pull the panel and its frame in by its width.
  var modalTimer = null;
  function slideModal(modal, opened) {
    var dialog = qs('[data-maquette-dialog]', modal);
    var overlay = qs('[data-maquette-overlay]', modal);
    root.clearTimeout(modalTimer);
    if (opened) {
      lockPage(true);
      show(modal, true);
      modal.classList.add('--opened');
      // Next frame: the closed position must be painted first for the slide to run
      root.requestAnimationFrame(function () {
        root.requestAnimationFrame(function () {
          dialog.setAttribute('data-popup-opened', '');
          overlay.classList.add('--visible');
        });
      });
    } else {
      dialog.removeAttribute('data-popup-opened');
      overlay.classList.remove('--visible');
      modalTimer = root.setTimeout(function () {
        modal.classList.remove('--opened');
        show(modal, false);
        lockPage(false);
      }, 300);
    }
  }

  function closeModal(modal) {
    if (!isOpen(modal) || isSending(modal)) return;
    // Closing during the QR step: their WhatsApp message still attaches on the server side
    stopQr();
    slideModal(modal, false);
    if (request.trigger && doc.documentElement.contains(request.trigger)) focus(request.trigger);
  }

  function trapFocus(modal, event) {
    var dialog = qs('[data-maquette-dialog]', modal);
    var nodes = qsa(FOCUSABLE, dialog).filter(isShown);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = doc.activeElement;
    // Focus outside (e.g. back on <body>): Tab comes back into the popup
    if (!dialog.contains(active)) {
      event.preventDefault();
      focus(event.shiftKey ? last : first);
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      focus(last);
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      focus(first);
    }
  }

  // ---------------------------------------------------------------------------
  // Request popup
  // ---------------------------------------------------------------------------

  function hasWhatsapp() { return !!pop.whatsapp || !!pop.preview; }

  function renderProduct(modal) {
    var snapshot = captureSnapshot();
    var image = qs('[data-maquette-product-image]', modal);
    // No product row outside product pages (snippets/maquette-request.liquid)
    if (!image) return;
    if (snapshot.product && snapshot.product.image) image.src = withWidth(snapshot.product.image, 200);
    // A model page shows the chosen device in an Easify « info-product » card (photo named after the device, brand
    // link): device photo, name and brand next to the model. A device page's own card has no photo: nothing to add.
    var cardBox = qsa('.productView-details .info-product').filter(isShown)[0];
    var cardImage = cardBox && qs('img', cardBox);
    var cardBrand = cardImage && qs('p a', cardBox);
    var tool = qs('[data-maquette-product-tool]', modal);
    if (cardImage) tool.src = cardImage.currentSrc || cardImage.src;
    show(tool, !!cardImage);
    var device = cardImage ? (cardImage.getAttribute('alt') || '').trim() : '';
    setMessage(qs('[data-maquette-product-device]', modal), device);
    setMessage(qs('[data-maquette-product-brand]', modal), cardBrand ? cardBrand.textContent.trim() : '');
    renderOptions(qs('[data-maquette-product-options]', modal), optionValues(snapshot).filter(function (value) { return value !== device; }));
    show(qs('[data-maquette-product-visual]', modal), snapshot.visual);
  }

  // Grey pill « ⌀ 41 mm / À droite »: the chosen option values, slash separated
  function renderOptions(node, values) {
    node.innerHTML = '';
    values.forEach(function (value, index) {
      if (index) node.appendChild(el('span', { 'aria-hidden': 'true' }, '/'));
      node.appendChild(el('span', {}, value));
    });
    show(node, values.length > 0);
  }

  // Product tab « modifier »: the popup closes on the product's options, the customer changes them on the page
  function editProduct(modal) {
    var target = qs('.productView-details .easify-product-options') || qs('.productView-details');
    request.trigger = null;
    closeModal(modal);
    if (!target) return;
    // Below the site's sticky header
    var header = qs('.section-header-navigation');
    var offset = (header ? header.getBoundingClientRect().height : 0) + 16;
    root.scrollTo({ top: target.getBoundingClientRect().top + root.pageYOffset - offset, behavior: 'smooth' });
    focus(qsa(FOCUSABLE, target).filter(isShown)[0], { preventScroll: true });
  }

  // ---------------------------------------------------------------------------
  // « En repartant de zéro »: the « Importer mon visuel » options (snippets/maquette-scratch.liquid) and the model gallery
  // ---------------------------------------------------------------------------

  function scratchForm(modal) { return qs('[data-maquette-scratch-form]', modal || requestModal()); }

  function checkedIn(form, name) { return qs('input[name="' + name + '"]:checked', form); }

  // Hidden by a condition of the form (not by the tab, which hides the whole form)
  function hiddenIn(form, node) {
    var hidden = node.closest('[hidden]');
    return !!hidden && form.contains(hidden);
  }

  // Like Easify's conditions: the type shows its options, the device its jaws, the jaw's shape its orientations,
  // the stamp's shape its sizes. A choice hidden by another one moves to the first one shown. Hidden fields are disabled.
  function renderScratch(modal) {
    var form = scratchForm(modal);
    if (!form) return;
    var device = qs('select[name="maquette-device"]', form);
    var state = {};
    function apply() {
      qsa('[data-maquette-when]', form).forEach(function (node) {
        var rule = node.getAttribute('data-maquette-when').split(':');
        show(node, rule[1].split(' ').indexOf(state[rule[0]]) !== -1);
      });
    }
    function settle(name) {
      var current = checkedIn(form, name);
      if (current && !hiddenIn(form, current)) return current;
      var first = qsa('input[name="' + name + '"]', form).filter(function (input) { return !hiddenIn(form, input); })[0];
      if (first) first.checked = true;
      return first || null;
    }
    state.kind = checkedIn(form, 'maquette-type').getAttribute('data-kind');
    state.device = device.options[device.selectedIndex].getAttribute('data-device');
    apply();
    var jaw = settle('maquette-jaw');
    state.shape = jaw ? jaw.getAttribute('data-shape') : '';
    state.stamp = checkedIn(form, 'maquette-shape').getAttribute('data-shape');
    state.inks = checkedIn(form, 'maquette-ink').getAttribute('data-inks');
    apply();
    settle('maquette-orientation');
    settle('maquette-size');
    qsa('input, select', form).forEach(function (field) { field.disabled = hiddenIn(form, field); });
  }

  function scratchKind(form) {
    var type = form && checkedIn(form, 'maquette-type');
    return type ? type.getAttribute('data-kind') : 'press';
  }

  // Easify property names and French values, in the product form's order (Type, Appareil, jaw, orientation…)
  function scratchProperties(form) {
    var out = {};
    function put(key, input) { if (key && input && !input.disabled) out[key] = input.value; }
    put('Type', checkedIn(form, 'maquette-type'));
    if (scratchKind(form) === 'press') {
      var device = qs('select[name="maquette-device"]', form);
      var jaw = checkedIn(form, 'maquette-jaw');
      put('Appareil', device);
      put(device.options[device.selectedIndex].getAttribute('data-jaw-key'), jaw);
      put(jaw && jaw.getAttribute('data-orientation-key'), checkedIn(form, 'maquette-orientation'));
    } else {
      var shape = checkedIn(form, 'maquette-shape');
      put('Forme du tampon', shape);
      put(shape.getAttribute('data-size-key'), checkedIn(form, 'maquette-size'));
      put('Ajouter un encreur', checkedIn(form, 'maquette-ink'));
      // « Encre VersaFine CLAIR Nocturne | 1, … » as the product form writes the inks (one of each here)
      var inks = qsa('input[name="maquette-inks"]:checked', form).filter(function (input) { return !input.disabled; });
      if (inks.length) out['Sélection des encreurs (color)'] = inks.map(function (input) { return input.value + ' | 1'; }).join(', ');
    }
    return out;
  }

  // The picked model is the request's product, else « Importer mon visuel » (printed by snippets/maquette-scratch.liquid)
  function scratchSnapshot(out) {
    var form = scratchForm();
    if (form) out.properties = scratchProperties(form);
    out.product = request.model.picked || readJson('[data-maquette-scratch-product]');
    return out;
  }

  function modelsUrl(modal) {
    var gallery = qs('[data-maquette-request-gallery]', modal);
    return gallery.getAttribute(scratchKind(scratchForm(modal)) === 'stamp' ? 'data-stamp-url' : 'data-press-url') || '';
  }

  function setModelMode(modal, mode) {
    request.model.open = mode === 'pick';
    if (!request.model.open) {
      request.model.picked = null;
      qsa('.maquette__model', modal).forEach(function (node) { node.setAttribute('aria-pressed', 'false'); });
    }
    qsa('[data-maquette-request-model-mode]', modal).forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-maquette-request-model-mode') === mode ? 'true' : 'false');
    });
    show(qs('[data-maquette-request-gallery]', modal), request.model.open);
    if (request.model.open) loadModels(modal);
  }

  // Collection products JSON (public storefront endpoint), 8 tiles at a time, the next page fetched when needed.
  // Another type starts a gallery of its own: a page the previous one still awaits is dropped. A gallery left
  // empty (first page failed) is fetched again.
  function loadModels(modal) {
    var url = modelsUrl(modal);
    if (!url || (request.model.url === url && request.model.items.length)) return;
    request.model = { open: true, picked: null, url: url, items: [], page: 0, done: false, loading: false };
    qs('[data-maquette-request-models]', modal).innerHTML = '';
    moreModels(modal);
  }

  function moreModels(modal) {
    var model = request.model;
    var list = qs('[data-maquette-request-models]', modal);
    var more = qs('[data-maquette-request-more]', modal);
    var shown = list.children.length;
    if (model.items.length >= shown + MODELS_STEP || model.done) {
      model.items.slice(shown, shown + MODELS_STEP).forEach(function (item) { list.appendChild(modelTile(modal, item)); });
      show(more, list.children.length < model.items.length || !model.done);
      return;
    }
    if (model.loading) return;
    model.loading = true;
    var url = model.url;
    model.page += 1;
    root.fetch(url + '/products.json?limit=' + MODELS_PAGE + '&page=' + model.page, { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        model.loading = false;
        if (request.model !== model) return;
        var products = data && Array.isArray(data.products) ? data.products : [];
        if (products.length < MODELS_PAGE) model.done = true;
        products.forEach(function (product) {
          var variant = product.variants && product.variants[0];
          var image = product.images && product.images[0];
          if (variant && image) model.items.push({ product: product, variant: variant, image: image.src });
        });
        moreModels(modal);
      }, function () {
        model.loading = false;
        if (request.model !== model) return;
        model.done = true;
        moreModels(modal);
      });
  }

  function modelTile(modal, item) {
    var li = el('li', {});
    var button = el('button', { type: 'button', className: 'maquette__model', 'aria-pressed': 'false', 'aria-label': item.product.title });
    var press = request.model.url === qs('[data-maquette-request-gallery]', modal).getAttribute('data-press-url');
    var image = el('img', { src: withWidth(item.image, 240), alt: '', width: '110', height: '110', loading: 'lazy' });
    if (press) image.className = 'maquette__model-press';
    button.appendChild(image);
    button.addEventListener('click', function () {
      var picked = request.model.picked && request.model.picked.handle === item.product.handle;
      qsa('.maquette__model', modal).forEach(function (node) { node.setAttribute('aria-pressed', 'false'); });
      button.setAttribute('aria-pressed', picked ? 'false' : 'true');
      request.model.picked = picked ? null : {
        handle: truncate(item.product.handle, 255),
        title: truncate(item.product.title, 255),
        productId: item.product.id,
        variantId: Number(item.variant.id),
        variantTitle: null,
        options: {},
        price: null,
        image: truncate(item.image, 2000),
        quantity: 1
      };
    });
    li.appendChild(button);
    return li;
  }

  // Tabs "Depuis ce produit" / "En repartant de zéro" (like plaque-deces), product pages only:
  // the product row goes with the first one, the scratch options and model gallery with the second
  function renderMode(modal) {
    show(qs('[data-maquette-request-tabs]', modal), !!request.hasProduct);
    qsa('[data-maquette-request-mode]', modal).forEach(function (tab) {
      var active = tab.getAttribute('data-maquette-request-mode') === request.mode;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    show(qs('[data-maquette-product-row]', modal), request.mode === 'product');
    show(qs('[data-maquette-request-scratch]', modal), request.mode === 'scratch');
  }

  function setMode(modal, mode) {
    request.mode = mode === 'scratch' ? 'scratch' : 'product';
    renderMode(modal);
    renderProduct(modal);
    renderTexts(modal);
  }

  function showRequestState(modal, name) {
    qsa('[data-maquette-request-state]', modal).forEach(function (node) {
      show(node, node.getAttribute('data-maquette-request-state') === name);
    });
    qs('.maquette__close', modal).disabled = name === 'sending';
  }

  function fieldError(field, invalid) {
    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', 'maquette-error');
    } else {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }
  }

  // Email field: always shown without WhatsApp, otherwise revealed by « Recevoir par email »
  function showEmailField(modal, visible) {
    show(qs('[data-maquette-request-email-field]', modal), visible);
    show(qs('[data-maquette-request-email-reveal]', modal), !visible);
  }

  function updateCounter(modal) {
    var length = qs('[data-maquette-request-text]', modal).value.length;
    var counter = qs('[data-maquette-request-counter]', modal);
    var locale = pop.locale || undefined;
    counter.textContent = length.toLocaleString(locale) + ' / ' + MAX_TEXT.toLocaleString(locale);
    show(counter, length > COUNTER_FROM);
  }

  function focusDoneTitle(block) {
    focus(qs('[data-maquette-request-done-title]', block));
  }

  function renderRequestDone(modal, result, channel, email, qr) {
    var response = result.response;
    var whatsappBlock = qs('[data-maquette-request-done-whatsapp]', modal);
    var emailBlock = qs('[data-maquette-request-done-email]', modal);
    var fallbackBlock = qs('[data-maquette-request-done-fallback]', modal);
    var qrBlock = qs('[data-maquette-request-done-qr]', modal);
    var hasLink = channel === 'whatsapp' && !!response.whatsappUrl;
    var withQr = hasLink && qr;
    show(qs('[data-maquette-request-done-received]', modal), false);
    show(qrBlock, withQr);
    // QR code: no « Continuer mes achats » while waiting, leaving there loses the request
    show(qs('[data-maquette-request-continue]', modal), !withQr);
    show(whatsappBlock, hasLink && !withQr);
    show(emailBlock, channel === 'email');
    show(fallbackBlock, channel === 'whatsapp' && !response.whatsappUrl);
    // « Ouvrir WhatsApp » only when the browser blocked the pre-opened WhatsApp tab
    var link = qs('[data-maquette-request-whatsapp-link]', modal);
    if (hasLink) link.href = response.whatsappUrl;
    show(link, hasLink && !withQr && result.whatsapp === 'blocked');
    if (channel === 'email') {
      var text = qs('[data-maquette-request-done-email-text]', modal);
      text.innerHTML = fill(text.getAttribute('data-template') || '', 'email', escapeHtml(email));
    }
    show(qs('[data-maquette-request-duplicate]', modal), result.duplicate);
    request.sent = true;
    showRequestState(modal, 'done');
    if (withQr) startQr(modal, qrBlock, response);
    focusDoneTitle(withQr ? qrBlock : (hasLink ? whatsappBlock : (channel === 'email' ? emailBlock : fallbackBlock)));
  }

  // ----- Desktop WhatsApp: QR code until the customer's message arrives -----
  // On a computer wa.me lands on a page asking for an app most customers do not have there.

  function prefersQrCode() {
    return typeof root.matchMedia === 'function' && !root.matchMedia('(pointer: coarse)').matches;
  }

  function loadQrLibrary(src) {
    if (typeof root.qrcode === 'function') return Promise.resolve(root.qrcode);
    if (!qrLibrary) {
      qrLibrary = new Promise(function (resolve, reject) {
        var script = el('script', { src: src });
        script.onload = function () { if (typeof root.qrcode === 'function') resolve(root.qrcode); else reject(new Error('qrcode')); };
        script.onerror = reject;
        doc.head.appendChild(script);
      }).then(null, function (error) {
        qrLibrary = null;
        throw error;
      });
    }
    return qrLibrary;
  }

  function drawQr(canvas, url, src) {
    show(canvas, true);
    loadQrLibrary(src).then(function (qrcode) {
      var code = qrcode(0, 'M');
      code.addData(url);
      code.make();
      var count = code.getModuleCount();
      // 2 white modules around (quiet zone), whole pixels per module: sharp
      var cell = Math.max(1, Math.floor(canvas.width / (count + 4)));
      var offset = Math.floor((canvas.width - cell * count) / 2);
      var context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#000000';
      for (var row = 0; row < count; row += 1) {
        for (var col = 0; col < count; col += 1) {
          if (code.isDark(row, col)) context.fillRect(offset + col * cell, offset + row * cell, cell, cell);
        }
      }
    }).then(null, function () {
      // No QR code: the « WhatsApp on this computer » link and email stay
      show(canvas, false);
    });
  }

  function stopQr() {
    if (!qrPoll) return;
    clearTimeout(qrPoll.timer);
    clearTimeout(qrPoll.hint);
    qrPoll = null;
  }

  function startQr(modal, block, response) {
    stopQr();
    previewPolls = 0;
    qs('[data-maquette-request-qr-app]', block).href = response.whatsappUrl;
    var hint = qs('[data-maquette-request-qr-hint]', block);
    show(hint, false);
    drawQr(qs('[data-maquette-request-qr]', block), shortWhatsappUrl(response.whatsappUrl), block.getAttribute('data-maquette-request-qr-script'));

    var poll = qrPoll = { startedAt: Date.now(), timer: null, hint: null };
    poll.hint = setTimeout(function () { show(hint, true); }, QR_HINT_MS);
    var url = ENDPOINT + '/' + encodeURIComponent(response.reference);
    function tick() {
      if (qrPoll !== poll) return;
      if (!isOpen(modal)) {
        stopQr();
        return;
      }
      // Gave up: the hint stays, no « received » will come
      if (Date.now() - poll.startedAt > QR_POLL_MAX_MS) {
        stopQr();
        show(hint, true);
        return;
      }
      fetchJson(url, { method: 'GET' }).then(function (result) {
        if (qrPoll !== poll) return;
        if (result.ok && result.data && result.data.received) {
          stopQr();
          showReceived(modal);
          return;
        }
        poll.timer = setTimeout(tick, QR_POLL_MS);
      });
    }
    poll.timer = setTimeout(tick, QR_POLL_MS);
  }

  function showReceived(modal) {
    clearFiles(modal);
    show(qs('[data-maquette-request-done-qr]', modal), false);
    var block = qs('[data-maquette-request-done-received]', modal);
    show(block, true);
    show(qs('[data-maquette-request-continue]', modal), true);
    focusDoneTitle(block);
  }

  // « Recevoir plutôt par email »: back to the form, text and files kept, only the address is missing
  function qrToEmail(modal) {
    stopQr();
    showRequestState(modal, 'form');
    showEmailField(modal, true);
    focus(qs('[data-maquette-request-email]', modal));
  }

  // `channelChoice`: 'whatsapp' (SYNCHRONOUS call from the click: the tab is pre-opened before any await)
  // or 'email' (form submit). Text, files, an attached Easify visual or a custom text are enough.
  function submitRequest(modal, channelChoice) {
    if (isSending(modal)) return;
    var textarea = qs('[data-maquette-request-text]', modal);
    var emailInput = qs('[data-maquette-request-email]', modal);
    var errorNode = qs('[data-maquette-request-error]', modal);
    var text = textarea.value.trim();
    var email = emailInput.value.trim();
    var channel = channelChoice === 'whatsapp' && hasWhatsapp() ? 'whatsapp' : 'email';
    var qr = channel === 'whatsapp' && prefersQrCode();
    // Captured at send time: the customer may change an Easify option while the popup is open
    var snapshot = captureSnapshot();
    setMessage(errorNode, '');
    fieldError(textarea, false);
    fieldError(emailInput, false);
    if (!text && !request.files.length && !snapshot.visual && !hasCustomText()) {
      setMessage(errorNode, t('err_empty'));
      fieldError(textarea, true);
      focus(textarea);
      return;
    }
    var emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    if (channel === 'email' && !emailValid) {
      showEmailField(modal, true);
      setMessage(errorNode, t('err_email'));
      fieldError(emailInput, true);
      focus(emailInput);
      return;
    }

    // WhatsApp tab pre-opened synchronously (popup blockers). None for the QR code or in preview mode.
    var popup = null;
    if (channel === 'whatsapp' && !qr && !pop.preview) {
      try { popup = root.open('about:blank', '_blank'); } catch (e) { popup = null; }
    }
    show(qs('[data-maquette-request-sending-files]', modal), request.files.length > 0);
    showRequestState(modal, 'sending');

    // WhatsApp: the address goes along only when valid (an unfinished one would fail the request)
    sendRequest(channel, emailValid ? email : '', text, snapshot, request.files.slice(), popup).then(function (result) {
      if (result.error) {
        showRequestState(modal, 'form');
        setMessage(errorNode, result.error);
        return;
      }
      // QR code: files kept until the message arrives (« Recevoir plutôt par email » resends them)
      if (!result.duplicate && !qr) clearFiles(modal);
      renderRequestDone(modal, result, channel, email, qr);
    });
  }

  function bindRequestModal(modal) {
    if (modal.getAttribute('data-maquette-bound')) return;
    modal.setAttribute('data-maquette-bound', '1');
    qs('[data-maquette-request-form]', modal).addEventListener('submit', function (event) {
      event.preventDefault();
      submitRequest(modal, 'email');
    });
    var input = qs('[data-maquette-request-files]', modal);
    input.addEventListener('change', function () {
      addFiles(modal, input.files);
      input.value = '';
    });
    qs('[data-maquette-request-text]', modal).addEventListener('input', function () { updateCounter(modal); });
    // Any scratch choice applies its conditions again; a new type (press / stamp) also changes the models on show
    var form = scratchForm(modal);
    if (form) form.addEventListener('change', function () {
      renderScratch(modal);
      if (request.model.open) loadModels(modal);
    });
    modal.addEventListener('click', function (event) {
      var target = event.target;
      if (target.closest('[data-maquette-close]')) {
        event.preventDefault();
        closeModal(modal);
      } else if (target.closest('[data-maquette-request-send="whatsapp"]')) {
        event.preventDefault();
        submitRequest(modal, 'whatsapp');
      } else if (target.closest('[data-maquette-request-qr-email]')) {
        event.preventDefault();
        qrToEmail(modal);
      } else if (target.closest('[data-maquette-request-email-reveal]')) {
        event.preventDefault();
        showEmailField(modal, true);
        focus(qs('[data-maquette-request-email]', modal));
      } else if (target.closest('[data-maquette-request-mode]')) {
        event.preventDefault();
        setMode(modal, target.closest('[data-maquette-request-mode]').getAttribute('data-maquette-request-mode'));
      } else if (target.closest('[data-maquette-request-edit]')) {
        event.preventDefault();
        editProduct(modal);
      } else if (target.closest('[data-maquette-request-model-mode]')) {
        event.preventDefault();
        setModelMode(modal, target.closest('[data-maquette-request-model-mode]').getAttribute('data-maquette-request-model-mode'));
      } else if (target.closest('[data-maquette-request-more]')) {
        event.preventDefault();
        moreModels(modal);
      } else if (target.closest('[data-maquette-scratch-help]')) {
        // « comment ça marche ? »: the orientation explanation of the product page, under the link
        var help = target.closest('[data-maquette-scratch-help]');
        var open = help.getAttribute('aria-expanded') !== 'true';
        help.setAttribute('aria-expanded', open ? 'true' : 'false');
        show(qs('#' + help.getAttribute('aria-controls'), modal), open);
      } else if (target.closest('[data-maquette-request-remove]')) {
        event.preventDefault();
        request.files.splice(Number(target.closest('[data-maquette-request-remove]').getAttribute('data-maquette-request-remove')), 1);
        renderPreviews(modal);
        focus(input);
      }
    });
  }

  function openRequest(trigger) {
    var modal = requestModal();
    if (!modal) return;
    bindRequestModal(modal);
    stopQr();
    request.trigger = trigger;
    request.mode = 'product';
    request.hasProduct = !!captureSnapshot().product;
    setModelMode(modal, 'none');
    renderScratch(modal);
    renderMode(modal);
    // Reopening: the text is kept unless it was sent, the files are cleared (no leak to another product)
    if (request.sent) qs('[data-maquette-request-text]', modal).value = '';
    request.sent = false;
    clearFiles(modal);
    setMessage(qs('[data-maquette-request-files-error]', modal), '');
    setMessage(qs('[data-maquette-request-error]', modal), '');
    renderProduct(modal);
    renderTexts(modal);
    fieldError(qs('[data-maquette-request-text]', modal), false);
    fieldError(qs('[data-maquette-request-email]', modal), false);
    showEmailField(modal, !hasWhatsapp());
    updateCounter(modal);
    showRequestState(modal, 'form');
    slideModal(modal, true);
    // Focus on the title, not the field: the phone keyboard would hide the explanation
    focus(qs('[data-maquette-focus]', modal));
  }

  // Studio status: a designer online promises the short delay. A class on <html>, not on the cards,
  // so the grid's ajax re-renders keep it. Unknown status (error, preview mode) = offline wording.
  function loadStudioStatus() {
    if (!pop.enabled || pop.preview || !root.fetch) return;
    root.fetch('/apps/maquette/status', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (status) {
        doc.documentElement.classList.toggle('maquette-studio-online', !!(status && status.online));
      })
      .catch(function () {});
  }
  loadStudioStatus();

  // Easify info boxes (HTML options, translated by global.js): the maquette wording.
  // Easify renders its options late and re-renders them on variant change: filled again on every DOM change.
  function fillHints() {
    var template = qs('template[data-maquette-hints]');
    qsa('.maquette-info-files, .maquette-info-text').forEach(function (box) {
      if (qs('.maquette-hint', box)) return;
      var kind = box.classList.contains('maquette-info-files') ? 'files' : 'text';
      box.innerHTML = template.content.querySelector('[data-maquette-hint="' + kind + '"]').innerHTML;
    });
  }
  if (pop.enabled && root.MutationObserver && qs('template[data-maquette-hints]')) {
    var hintsQueued = false;
    new root.MutationObserver(function () {
      if (hintsQueued) return;
      hintsQueued = true;
      root.requestAnimationFrame(function () { hintsQueued = false; fillHints(); });
    }).observe(doc.body, { childList: true, subtree: true });
    fillHints();
  }

  // Listing card (snippets/maquette-request.liquid, part 'card'): its tile's CSS height (assets/maquette.css) assumes
  // a 74px title/price zone, but product titles and « N compatible stamps » wrap on narrow columns and in other languages,
  // so the product cards run taller. Packery stacks the masonry grid by column: a shorter card shifts every card after it.
  // The tile takes the product cards' most common height (double-height masonry items and odd ones aside), then Packery
  // places the items again. Product cards change height on resize and font load: watched, not computed once.
  // A card alone on its row (full-width layout on phones) has no column to level: its tile keeps its CSS height.
  function levelListingCards() {
    qsa('.productListing > .maquette-card').forEach(function (card) {
      var item = qs('.maquette-card__item', card);
      var tile = qs('.maquette-card__tile', card);
      if (tile && card.getBoundingClientRect().width > card.parentElement.clientWidth * 0.6) {
        tile.style.height = '';
        return;
      }
      var counts = {};
      var target = 0;
      qsa('.product-item', card.parentElement).forEach(function (productItem) {
        if (productItem.parentElement.parentElement !== card.parentElement) return;
        var height = productItem.getBoundingClientRect().height;
        if (!height) return;
        var key = height.toFixed(2);
        counts[key] = (counts[key] || 0) + 1;
        if (!target || counts[key] > counts[target] || (counts[key] === counts[target] && height > +target)) target = key;
      });
      if (!target || !item || !tile) return;
      var style = root.getComputedStyle(item);
      var height = (+target - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)) + 'px';
      if (tile.style.height === height) return;
      tile.style.height = height;
      var packery = root.jQuery && root.jQuery(card.parentElement).data('packery');
      if (packery) packery.layout();
    });
  }
  if (root.ResizeObserver && qs('.productListing > .maquette-card')) {
    var levelQueued = false;
    var levelObserver = new root.ResizeObserver(function () {
      if (levelQueued) return;
      levelQueued = true;
      root.requestAnimationFrame(function () { levelQueued = false; levelListingCards(); });
    });
    qsa('.productListing > .maquette-card').forEach(function (card) {
      qsa('.product-item', card.parentElement).forEach(function (productItem) { levelObserver.observe(productItem); });
    });
    levelListingCards();
  }

  // Product page: the bottom-right widget (sections/popup-maquette.liquid) opens once the « Besoin d'aide ? » block
  // of the title row (else the « Ajouter au panier » button) is scrolled past, and closes when scrolling back to it.
  // Elsewhere it opens on click only. Same attribute and class as AddPopupsProcess openPopup / closePopup (assets/global.js).
  function autoOpenWidget() {
    var ask = qs('.productView-details .maquette-ask');
    var button = qs('.productView-details .product-form__submit');
    var popup = qs('[data-popup="maquette"]');
    if (!(ask || button) || !popup) return;
    var passed = false;
    var queued = false;
    var closeTimer = null;
    var dismissed = false;
    // Minimized by hand: no more auto-open on this page
    var minimize = qs('[data-popup-minimize]', popup);
    if (minimize) minimize.addEventListener('click', function () { dismissed = true; });
    // Scroll position, not an IntersectionObserver: a jump over the button (anchor, back to top) must count too
    function update() {
      queued = false;
      // Opens once the block is above the screen, closes once it is back 80px into view: no flapping on small scrolls
      // The block is hidden on mobile (assets/maquette.css): the button takes over
      var target = ask && isShown(ask) ? ask : button;
      if (!target) return;
      var bottom = target.getBoundingClientRect().bottom;
      var nowPassed = passed ? bottom < 80 : bottom < 0;
      if (nowPassed === passed) return;
      passed = nowPassed;
      if (dismissed) return;
      root.clearTimeout(closeTimer);
      if (passed) {
        popup.parentElement.classList.add('--opened');
        popup.setAttribute('data-popup-opened', '');
      } else {
        popup.removeAttribute('data-popup-opened');
        closeTimer = root.setTimeout(function () { popup.parentElement.classList.remove('--opened'); }, 500);
      }
    }
    root.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      root.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }
  if (pop.enabled) autoOpenWidget();

  // Delegated listeners: entry points may be re-rendered by the theme
  doc.addEventListener('click', function (event) {
    var target = event.target.closest ? event.target.closest('[data-maquette-open]') : null;
    if (!target || !pop.enabled) return;
    event.preventDefault();
    openRequest(target);
  });
  // On the document, not the modal: Tab must stay trapped even when focus fell back to <body>
  doc.addEventListener('keydown', function (event) {
    var modal = requestModal();
    // Hidden, or sliding out (« modifier », close): the page has the keyboard again
    if (!isOpen(modal) || !qs('[data-maquette-dialog]', modal).hasAttribute('data-popup-opened')) return;
    if (event.key === 'Escape') closeModal(modal);
    else if (event.key === 'Tab') trapFocus(modal, event);
    else if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.closest && event.target.closest('[data-maquette-request-mode]')) {
      // Two tabs: either arrow goes to the other one (roving tabindex)
      event.preventDefault();
      setMode(modal, request.mode === 'product' ? 'scratch' : 'product');
      focus(qs('[data-maquette-request-mode="' + request.mode + '"]', modal));
    }
  });
})(window);
